// Component: utils.js
// Purpose: Shared SmartChart data, presentation, and synchronization helpers.

/**
 * Extracts the specified field value from OHLCV data point.
 * Used for both historical data processing and real-time tick updates.
 * 
 * @param {Object} dataPoint - OHLCV data point (historical or live candle)
 * @param {string} field - Field name (OPEN, HIGH, CLOSE, VWAP, etc.)
 * @param {boolean} shouldConvertToNumber - If true, converts to Number (for historical data)
 * @returns {number|undefined} Numeric value of the field, or undefined if field doesn't exist
 * 
 * @note VWAP and TRADE_COUNT availability depends on broker and data source.
 *       No fallback values - returns undefined if field is missing.
 *       Calculated fields (HL2, HLC3, OHLC4, OC2) are always available.
 */
// Optimization: Pre-compile regex for performance
const NON_NUMERIC_REGEX = /[^0-9.-]/g;

//! 142-42 — ONE price-scale width for the main chart and every sub-pane (vertical alignment), and
//! the right inset of in-canvas controls (scroll-to-latest) so they never sit on the scale labels.
export const PRICE_SCALE_WIDTH = 72; // fits 5-digit prices (e.g. 9458.50) without a blank band; shared by every pane

//! 50-11 — ONE authority for every collapsed/expanded pane size. A full sub-pane is PANE_HEIGHT; in the
//* collapsed card each pane is a fixed PANE_STRIP_HEIGHT row (at most MAX_COLLAPSED_STRIPS rows, the last
//* becoming a '+N more' row when there are more panes); the main canvas never drops below MAIN_CANVAS_MIN_PX.
export const PANE_HEIGHT = 100;
export const PANE_STRIP_HEIGHT = 44;
export const MAX_COLLAPSED_STRIPS = 3;
export const MAIN_CANVAS_MIN_PX = 200;
//* 50-13 — below this candle-pane height every marker is glyph-only (a colliding cluster shows one 'N events' label).
export const MARKER_LABEL_MIN_CANVAS_PX = 260;

//--> Keep the live-path volume rule in one place. Live bars use `volume` when
//*   present; `value` is the legacy fallback used by the existing update path.
export const barVolume = (bar) => (
    bar?.volume !== undefined ? bar.volume : bar?.value
);

//--> Reapply the live tail after a history snapshot replaces chart data.
//*   History owns closed buckets. The one forming boundary bucket unions price
//*   while keeping persisted volume; newer buckets remain live.
export const mergeLiveTail = ({ historyBars = [], liveBars = [], isHeikin = false }) => {
    if (!Array.isArray(historyBars) || historyBars.length === 0) {
        return Array.isArray(liveBars) ? [...liveBars] : [];
    }

    const history = [...historyBars].sort((a, b) => a.time - b.time);
    const live = Array.isArray(liveBars)
        ? [...liveBars].sort((a, b) => a.time - b.time)
        : [];
    const historyLast = history[history.length - 1];
    const highWater = historyLast.time;
    const liveLastTime = live.length > 0 ? live[live.length - 1].time : null;
    const liveAtHighWater = live.find((item) => item.time === highWater);

    let boundary = historyLast;
    if (!isHeikin && liveAtHighWater && liveLastTime === highWater) {
        boundary = {
            ...historyLast,
            open: historyLast.open,
            high: Math.max(historyLast.high, liveAtHighWater.high),
            low: Math.min(historyLast.low, liveAtHighWater.low),
            close: liveAtHighWater.close,
            volume: historyLast.volume,
            value: historyLast.value,
        };
    }

    return [
        ...history.slice(0, -1),
        boundary,
        ...live.filter((item) => item.time > highWater),
    ];
};

export const extractFieldValue = (dataPoint, field = 'CLOSE', shouldConvertToNumber = false) => {
    // 0. Sanitize Field Key (Fast check)
    if (!field) return undefined;
    const cleanField = field.trim();
    if (!cleanField) return undefined;

    // Helper: Safe Number Conversion (Optimized)
    const safeNumber = (v) => {
        if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
        if (typeof v === 'string') {
            // Empty/whitespace should be treated as missing data, not 0
            const trimmed = v.trim();
            if (!trimmed) return undefined;

            // Fast path for clean numeric strings
            const direct = Number(trimmed);
            if (Number.isFinite(direct)) return direct;

            // Slow path: sanitize "dirty" strings (e.g., "$1,200.50")
            const sanitized = parseFloat(trimmed.replace(NON_NUMERIC_REGEX, ''));
            return Number.isFinite(sanitized) ? sanitized : undefined;
        }
        return undefined;
    };

    // 1. Handle Dot Notation (e.g. "metadata.implied_volatility")
    if (cleanField.includes('.')) {
        const parts = cleanField.split('.');
        let val = dataPoint;
        for (let i = 0; i < parts.length; i++) {
            if (!val) return undefined;
            val = val[parts[i]];
        }
        return val !== undefined ? (shouldConvertToNumber ? safeNumber(val) : val) : undefined;
    }

    // 2. Direct Property Access (Optimized)
    // Most common case: exact match (close, CLOSE, etc)
    const exactVal = dataPoint[cleanField];
    if (exactVal !== undefined) {
        return shouldConvertToNumber ? safeNumber(exactVal) : exactVal;
    }

    const f = cleanField.toUpperCase();
    const convert = shouldConvertToNumber ? safeNumber : (v) => v;
    
    switch (f) {
        // Standard OHLCV fields
        case 'OPEN': 
            return dataPoint.open !== undefined ? convert(dataPoint.open) : undefined;
        case 'HIGH': 
            return dataPoint.high !== undefined ? convert(dataPoint.high) : undefined;
        case 'LOW': 
            return dataPoint.low !== undefined ? convert(dataPoint.low) : undefined;
        case 'CLOSE': 
            return dataPoint.close !== undefined ? convert(dataPoint.close) : undefined;
        case 'VOLUME': 
            // Volume might be in 'volume' or 'value' field depending on source
            return dataPoint.volume !== undefined ? convert(dataPoint.volume) : 
                   (dataPoint.value !== undefined ? convert(dataPoint.value) : undefined);
        
        // Extended fields (broker-dependent)
        case 'VWAP': 
            return dataPoint.vwap !== undefined ? convert(dataPoint.vwap) : undefined;
        case 'TRADE_COUNT': 
            return dataPoint.trade_count !== undefined ? convert(dataPoint.trade_count) : undefined;
        
        // Calculated/Derived fields (always available if base OHLC exists)
        case 'HL2': {
            const high = convert(dataPoint.high);
            const low = convert(dataPoint.low);
            return (high !== undefined && low !== undefined) ? (high + low) / 2 : undefined;
        }
        case 'HLC3': {
            const high = convert(dataPoint.high);
            const low = convert(dataPoint.low);
            const close = convert(dataPoint.close);
            return (high !== undefined && low !== undefined && close !== undefined) 
                ? (high + low + close) / 3 : undefined;
        }
        case 'OHLC4': {
            const open = convert(dataPoint.open);
            const high = convert(dataPoint.high);
            const low = convert(dataPoint.low);
            const close = convert(dataPoint.close);
            return (open !== undefined && high !== undefined && low !== undefined && close !== undefined)
                ? (open + high + low + close) / 4 : undefined;
        }
        case 'OC2': {
            const open = convert(dataPoint.open);
            const close = convert(dataPoint.close);
            return (open !== undefined && close !== undefined) ? (open + close) / 2 : undefined;
        }
        
        default: 
            // FINAL ATTEMPT: Check for lowercase version of the field in case of case mismatch
            // e.g. "Poc_Price" -> "poc_price"
            const lowerF = field.toLowerCase();
            if (dataPoint[lowerF] !== undefined) {
                 return convert(dataPoint[lowerF]);
            }
            return undefined; // Do NOT fallback to CLOSE silently, as it hides errors
    }
};

/**
 * Sanitizes chart data by removing invalid zero-value points that distort scaling.
 * 
 * @param {Array} data - Array of chart data points (candles or line/bar items)
 * @returns {Array} - Cleaned array with zero-values filtered out
 */
export const sanitizeChartData = (data) => {
    if (!Array.isArray(data)) return [];
    
    return data.map(d => {
        if (!d) return null;
        
        // 1. Check for Line/Histogram data (single 'value' field)
        if (d.value !== undefined) {
             // 1. Scalar Value Data (Indicators/Histograms)
             // We only filter if value is fundamentally invalid (non-numeric).
             if (typeof d.value === 'boolean') return d;
             if (typeof d.value !== 'number' || isNaN(d.value) || !isFinite(d.value)) {
                 return d.time !== undefined ? { time: d.time } : null;
             }
             return d;
        }
        
        // 2. Check for Candlestick/Bar data (Open, High, Low, Close)
        if (d.open !== undefined) {
             // Ensure values are finite numbers and not zero (invalid for price)
             if (
                 !Number.isFinite(d.open) || d.open === 0 ||
                 !Number.isFinite(d.high) || d.high === 0 ||
                 !Number.isFinite(d.low) || d.low === 0 ||
                 !Number.isFinite(d.close) || d.close === 0
             ) {
                 return d.time !== undefined ? { time: d.time } : null;
             }
             return d;
        }
        
        // 3. Allow Whitespace (Time Only)
        // If it has time but NO value and NO ohlc, it's valid whitespace (used for alignment)
        if (d.time !== undefined && d.value === undefined && d.open === undefined) {
             return { time: d.time }; // stripped of undefined keys explicitly
        }
        
        return null;
    }).filter(d => d !== null);
};
/**
 * Event Emitter for High-Performance Chart Synchronization.
 * Bypasses React State to avoid re-renders (Crosshair & Zoom/Pan).
 * Now supports Cross-Window Synchronization via BroadcastChannel (Pop-out).
 */
export class ChartSyncService {
    constructor(strategyId) {
        this.crosshairSubscribers = new Set();
        this.rangeSubscribers = new Set();
        
        // State retention for late subscribers
        this.lastCrosshair = null;
        this.lastRange = null;
        
        // Uniquely identify this window/instance to prevent echo loops
        this.instanceId = Math.random().toString(36).substr(2, 9);
        
        // Initialize BroadcastChannel if strategyId is provided
        if (strategyId) {
            this.channelName = `chart_sync_${strategyId}`;
            this.channel = new BroadcastChannel(this.channelName);
            
            this.channel.onmessage = (event) => {
                const { type, payload, sourceInstanceId } = event.data;
                
                // Prevent Echo: Don't process our own messages
                if (sourceInstanceId === this.instanceId) return;
                
                if (type === 'CROSSHAIR_MOVE') {
                    this.crosshairSubscribers.forEach(cb => cb(payload));
                } else if (type === 'VISIBLE_RANGE_CHANGE') {
                    // Update Local State Cache for late subscribers in this window
                    this.lastRange = payload;
                    this.rangeSubscribers.forEach(cb => cb(payload));
                } else if (type === 'STRUCTURE_UPDATE') {
                    if (this.onStructureUpdate) this.onStructureUpdate(payload);
                } else if (type === 'STRUCTURE_REQUEST') {
                    if (this.onStructureRequest) this.onStructureRequest();
                }
            };
        }
    }

    // Graph Structure Sync (Nodes/Edges)
    emitStructure(payload) {
        if (this.channel) {
            this.channel.postMessage({
                type: 'STRUCTURE_UPDATE',
                payload,
                sourceInstanceId: this.instanceId
            });
        }
    }

    requestStructure() {
        if (this.channel) {
            this.channel.postMessage({
                type: 'STRUCTURE_REQUEST',
                sourceInstanceId: this.instanceId
            });
        }
    }

    onStructure(callback) {
        this.onStructureUpdate = callback;
    }

    onSetStructureRequest(callback) {
        this.onStructureRequest = callback;
    }

    // Crosshair
    subscribe(callback) {
        this.crosshairSubscribers.add(callback);
        return () => this.crosshairSubscribers.delete(callback);
    }

    emit(param) {
        // 1. Notify Local Subscribers
        this.crosshairSubscribers.forEach(callback => callback(param));
        
        // 2. Broadcast to other windows
        if (this.channel) {
            this.channel.postMessage({
                type: 'CROSSHAIR_MOVE',
                payload: param,
                sourceInstanceId: this.instanceId
            });
        }
    }

    // Visible Range (Zoom/Pan)
    subscribeRange(callback) {
        this.rangeSubscribers.add(callback);
        // REPLAY LAST STATE: Crucial for late-loading Subcharts to sync with where the User is looking.
        if (this.lastRange) callback(this.lastRange);
        return () => this.rangeSubscribers.delete(callback);
    }

    emitRange(range) {
        // Cache state immediately (late subscribers replay it)
        this.lastRange = range;
        //* Perf (Deepak 10-24, "drag is sluggish"): lightweight-charts fires the range listener on EVERY
        //* mouse event (a high-rate mouse = hundreds/s) and each follower's setVisibleLogicalRange is a
        //* synchronous model recompute. Coalesce the fan-out to ONE apply per animation frame with the
        //* latest range — the standard throttle for follower charts; the drawing was already per-frame.
        this._pendingRange = range;
        if (this._rangeFrame != null) return;
        const schedule = (typeof requestAnimationFrame === 'function') ? requestAnimationFrame : (cb) => setTimeout(cb, 0);
        this._rangeFrame = schedule(() => {
            this._rangeFrame = null;
            const latest = this._pendingRange;
            this._pendingRange = null;
            if (!latest) return;
            // 1. Notify Local Subscribers
            this.rangeSubscribers.forEach(callback => callback(latest));
            // 2. Broadcast to other windows
            if (this.channel) {
                this.channel.postMessage({
                    type: 'VISIBLE_RANGE_CHANGE',
                    payload: latest,
                    sourceInstanceId: this.instanceId
                });
            }
        });
    }
    
    destroy() {
        if (this._rangeFrame != null && typeof cancelAnimationFrame === 'function') { try { cancelAnimationFrame(this._rangeFrame); } catch (e) {} }
        this._rangeFrame = null; this._pendingRange = null;
        if (this.channel) {
            this.channel.close();
        }
        this.crosshairSubscribers.clear();
        this.rangeSubscribers.clear();
    }
}

//--> Synced visible ranges are LOGICAL (bar-index) ranges, and an index only means something against the
//*   data it was measured on: when N older bars are prepended every index shifts by N. A range emitted by a
//*   chart before its prepend (a coalesced fan-out still pending), or by a pane whose re-render with the
//*   older page lands a frame after the main chart's, would otherwise move the receiver N bars — the
//*   "load older history -> view snaps to the right end and goes empty" class (Deepak 10-25).
//*   Emitters stamp a range with one of their own bar times and that bar's index; receivers rebase it onto
//*   their own index of that time, and skip it when they do not hold that bar (data a generation apart).
//*   An un-stamped range (another sender, e.g. BacktestEquityCurve) passes through unchanged.
export const anchorRange = (range, timeScale, anchorTime) => {
    if (!range || anchorTime == null) return range;
    const anchorIndex = timeScale?.timeToIndex?.(anchorTime, false);
    if (anchorIndex == null) return range;
    return { from: range.from, to: range.to, anchorTime, anchorIndex };
};

export const rebaseRange = (range, timeScale) => {
    if (!range) return null;
    if (range.anchorTime == null) return range;
    const ownIndex = timeScale?.timeToIndex?.(range.anchorTime, false);
    if (ownIndex == null) return null; // receiver does not hold the emitter's anchor bar: not comparable
    const shift = ownIndex - range.anchorIndex;
    return { from: range.from + shift, to: range.to + shift };
};

//--> Echo rule for range-sync receivers. lightweight-charts applies setVisibleLogicalRange on its NEXT draw
//*   frame and fires the range listener there — never inside the call — so a boolean lock around the call
//*   is already released when the echo lands, and the echo (one frame stale) went back out on the shared
//*   service, replaced the leader's newer coalesced range and stopped its scroll animation (the "»" button
//*   did nothing). A receiver remembers the range it applied and does not re-broadcast that range.
export const isSameRange = (a, b) => !!a && !!b && Math.abs(a.from - b.from) < 0.01 && Math.abs(a.to - b.to) < 0.01;

/**
 * Helper to get trend color based on direction string.
 * Used for consistency across Trend Slope overlays and markers.
 * 
 * @param {string} trend - 'up', 'down', 'sideways', 'neutral'
 * @returns {string} Hex color
 */
export const getTrendColor = (trend) => {
    if (!trend) return '#6B7280';
    switch (trend.toLowerCase()) {
        case 'up': return '#10B981'; // Green
        case 'down': return '#EF4444'; // Red
        case 'sideways': return '#9CA3AF'; // Gray
        default: return '#6B7280';
    }
};
